import signOutIcon from "@assets/images/signOut.png";
import { Button, Menu } from "@material-ui/core";
import { AccountCircleOutlined, DescriptionOutlined } from "@material-ui/icons";
import CheckIcon from "@material-ui/icons/Check";
import { useUserPermissions } from "@saleor/auth/hooks/useUserPermissions";
import { Company, Zone } from "@saleor/auth/types";
import MyTypography from "@saleor/components/MyTypography";
import { PermissionEnum, UserFragment } from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import { getUserName } from "@saleor/misc";
import { privacyPolicyUrl } from "@saleor/privacyPolicy/urls";
import classNames from "classnames";
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState
} from "react";
import { useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    root: {
      padding: "0.8rem 1.6rem",
      "&:hover": {
        background: theme.palette.background.default,
        mixBlendMode: "multiply",
        borderRadius: "4px"
      },
      "&:active": {
        outline: "3px solid rgba(1, 178, 237, 0.24)"
      },
      display: "flex",
      color: theme.palette.purple.main,
      "& .MuiTypography-root": {
        color: theme.palette.purple.main
      },
      cursor: "pointer",
      alignItems: "center"
    },
    userName: {
      marginRight: 20
    },
    avaIcon: {
      display: "flex",
      marginLeft: "auto",
      "& .MuiSvgIcon-root": {
        marginRight: "0px"
      }
    },
    tooltip: {
      display: "flex",
      width: 250
    },
    tooltipBubble: {
      marginRight: 10
    },
    tooltipContent: {},
    contentText: {
      fontSize: 13
    },
    contentAction: {
      textAlign: "right"
    },
    btnOut: {
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      padding: "2rem 1.4rem",
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      width: "100%",
      justifyContent: "left",
      "& img": {
        marginRight: "1rem",
        padding: "3px 4px 3px 0px"
      }
    },
    btnPolicy: {
      cursor: "pointer",
      alignItems: "center",
      // padding: "2rem 1.4rem",
      "& .MuiTypography-root": {
        padding: "7px 12px"
      }
    },
    btnSaveDraf: {
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      padding: "2rem 1.4rem",
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      width: "100%",
      justifyContent: "left",
      "& svg": {
        marginRight: "1rem"
      }
    },
    roles: {
      "& .MuiTypography-root": {
        padding: "0.8rem 1.4rem",
        "&:last-child": {
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          paddingBottom: "1rem"
        }
      }
    },
    roleItem: {
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      "&.active": {
        color: theme.palette.info.main
      }
    }
  }),
  {
    name: "UserChip"
  }
);

export interface UserChipProps {
  user: UserFragment;
  onLogout: () => void;
  onProfileClick: () => void;
  setRole: Dispatch<SetStateAction<Zone>>;
  role: string;
  onSaveAndDrafClick?: () => void;
  listCompany: Company[];
  company: string;
  setCompany: (string) => void;
  setSoldToId: (string) => void;
}

const UserChip: FC<UserChipProps> = ({
  user,
  onLogout,
  setRole,
  role,
  onSaveAndDrafClick,
  listCompany,
  company,
  setCompany,
  setSoldToId
}) => {
  const classes = useStyles({});
  const [anchorEl, setAnchorEl] = useState(null);
  const intl = useIntl();
  const permissions = useUserPermissions();
  const navigate = useNavigator();
  const [, dispatchAppState] = useAppState();

  const viewDraftOrderPermission = permissions?.find(
    perm => perm.code === PermissionEnum.VIEW_DRAFT_ORDER
  );
  const extensionsPermissionExport = permissions?.find(
    perm => perm.code === PermissionEnum.VIEW_ORDER_EXPORT
  );
  const extensionsPermissionDomestic = permissions?.find(
    perm => perm.code === PermissionEnum.VIEW_ORDER_DOMESTIC
  );
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onPrivacyPolicyClick = () => {
    navigate(privacyPolicyUrl);
    handleClose();
    // window.open("https://www.scgpackaging.com/en/privacy-notice", "__blank");
  };
  const onCookiePolicyClick = () => {
    window.open(
      "https://www.scgpackaging.com/assets/file/cookie-notice/TH_PKG_Final_Cookie%20Policy_020420.pdf",
      "_blank",
      "noopener"
    );
  };

  useEffect(() => {
    if (listCompany.length) {
      dispatchAppState({
        payload: {
          showPoUpload: listCompany[0]?.showPoUpload
        },
        type: "showPoUpload"
      });
    }
  }, [listCompany]);

  return (
    <>
      <Button
        data-testid="userChip-profile"
        className={classes.root}
        onClick={handleClick}
      >
        <div className={classes.userName}>
          <MyTypography variant="body2">{getUserName(user, true)}</MyTypography>
        </div>
        <div className={classes.avaIcon}>
          <AccountCircleOutlined />
        </div>
      </Button>
      <Menu
        data-testid="userChip-menu"
        elevation={0}
        getContentAnchorEl={null}
        open={!!anchorEl}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        PaperProps={{
          style: {
            width: "255px",
            marginTop: "1.6rem",
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
            borderRadius: "8px"
          }
        }}
      >
        {extensionsPermissionExport && extensionsPermissionDomestic ? (
          <div className={classes.roles}>
            <MyTypography variant="subtitle3">
              {intl.formatMessage({ defaultMessage: "Switch roles" })}
            </MyTypography>
            <MyTypography
              variant="body3"
              onClick={() => setRole(Zone.Domestic)}
              className={classNames(classes.roleItem, {
                active: role === Zone.Domestic
              })}
            >
              {intl.formatMessage({ defaultMessage: "Domestic" })}
              {role === Zone.Domestic ? <CheckIcon /> : null}
            </MyTypography>
            <MyTypography
              variant="body3"
              onClick={() => setRole(Zone.Export)}
              className={classNames(classes.roleItem, {
                active: role === Zone.Export
              })}
            >
              {intl.formatMessage({ defaultMessage: "Export" })}
              {role === Zone.Export ? <CheckIcon /> : null}
            </MyTypography>
          </div>
        ) : null}
        {(listCompany || []).length > 1 && (
          <div className={classes.roles}>
            <MyTypography variant="subtitle3">
              {intl.formatMessage({ defaultMessage: "Switch Company" })}
            </MyTypography>
            {(listCompany || []).map((c, index) => (
              <MyTypography
                key={index}
                variant="body3"
                onClick={() => {
                  setCompany(c?.soldToCode);
                  setSoldToId(c?.id);
                  window.localStorage.setItem("SOLD_TO_ID", c?.id);
                  dispatchAppState({
                    payload: {
                      showPoUpload: c?.showPoUpload
                    },
                    type: "showPoUpload"
                  });
                  navigate("/");
                }}
                className={classNames(classes.roleItem, {
                  active: company === c?.soldToCode
                })}
              >
                {c?.soldToName}
                {/* {intl.formatMessage({ defaultMessage: "Company 1" })} */}
                {company === c?.soldToCode ? <CheckIcon /> : null}
              </MyTypography>
            ))}
          </div>
        )}
        {viewDraftOrderPermission && (
          <Button className={classes.btnSaveDraf} onClick={onSaveAndDrafClick}>
            <>
              <DescriptionOutlined viewBox="4 0 24 24" />
              <MyTypography variant="body3">
                บันทึกฉบับร่าง - คำสั่งซื้อ
              </MyTypography>
            </>
          </Button>
        )}
        <Button className={classes.btnOut} onClick={onLogout}>
          <>
            <img src={signOutIcon} alt={""} />
            <MyTypography variant="body3">
              {extensionsPermissionExport
                ? intl.formatMessage({ defaultMessage: "Log out" })
                : intl.formatMessage({ defaultMessage: "ออกจากระบบ" })}
            </MyTypography>
          </>
        </Button>
        <div className={classes.btnPolicy}>
          <>
            <MyTypography onClick={onPrivacyPolicyClick} variant="caption">
              {intl.formatMessage({
                defaultMessage: "นโยบายความเป็นส่วนตัว"
              })}
            </MyTypography>
            <MyTypography onClick={onCookiePolicyClick} variant="caption">
              {intl.formatMessage({ defaultMessage: "นโยบายการใช้คุ้กกี้" })}
            </MyTypography>
          </>
        </div>
      </Menu>
    </>
  );
};
UserChip.displayName = "UserChip";
export default UserChip;
